import * as React from 'react';

import styled from '@emotion/styled';

import { Column as BaseColumn, Row as BaseRow } from '~/components/Layout/Grid';
import { type ResponsiveBreakPoints } from '~/constants/responsive';
import { colors } from '~/styles/constants';

type Collapsible = 'always' | 'never' | ResponsiveBreakPoints;

const Section = styled('section')`
  border: 1px solid ${colors.greyBorder};
  border-radius: 5px;
  margin-bottom: 20px;
`;

const Header = styled('header')`
  background-color: #fbfbfb;
  border-bottom: 1px solid ${colors.greyBorder};
  padding: 15px 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  -webkit-border-top-left-radius: 5px;
  -webkit-border-top-right-radius: 5px;
  -moz-border-radius-topleft: 5px;
  -moz-border-radius-topright: 5px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
`;

const Title = styled('h4')`
  font-size: 16px;
  color: ${colors.gray1000};
  padding: 0;
  margin: 0;
  flex-grow: 1;
  font-weight: 400;
`;

const Addons = styled('div')`
  flex-shrink: 1;
`;

const Description = styled('span')`
  display: block;
  font-size: 14px;
  color: ${colors.gray1000};
  margin-bottom: 20px;
  padding: 0;
  line-height: 1em;
`;

const Body = styled('div')<{ collapsible: Collapsible; isCollapsed: boolean; noPadding: boolean }>`
  ${({ noPadding }) => (noPadding ? 'padding: 0;' : 'padding: 16px;')} box-sizing: border-box;
  display: block;
  height: 100%;
  color: ${colors.gray1000};

  ${({ collapsible, isCollapsed }) =>
    collapsible === 'never'
      ? 'display: block;'
      : collapsible === 'always'
      ? `display: ${isCollapsed ? 'none' : 'block'};`
      : `@media only screen and (max-width: ${collapsible}px) {
  display: ${isCollapsed ? 'none' : 'block'};
}`};
`;

interface RowProps {
  wrappable?: ResponsiveBreakPoints | boolean;
}

export const Row = styled(BaseRow)<RowProps>`
  ${({ wrappable }) =>
    wrappable
      ? wrappable === true
        ? `
        flex-wrap: wrap;
      `
        : `
        @media only screen and (max-width: ${wrappable}px) {
          flex-direction: column;
        }
      `
      : null}
  ${({ wrappable }) =>
    wrappable
      ? `
    margin-top: -20px;

    & > * {
      margin-top: 20px;
      max-width: 100%;
    }`
      : null}
  margin-bottom: 30px;

  &:last-of-type {
    margin-bottom: 0px;
  }
`;

interface ColumnProps {
  noPadding?: boolean;
  width?: string;
}

export const Column = styled(BaseColumn)<ColumnProps>`
  ${({ noPadding }) =>
    noPadding
      ? null
      : `
    &:last-of-type {
      padding-left: 8px;
    }

    &:first-of-type {
      padding-left: 0px;
    }`}
  ${({ width }) => (width ? `width: ${width}` : '')}
`;

export const InputContainer = styled('div')`
  margin-bottom: 20px;

  &:last-of-type {
    margin-bottom: 0px;
  }
`;

const CollapseIcon = styled('i')<{ collapsible: Collapsible }>`
  margin-left: 10px;
  font-size: 18px;
  display: none;

  ${({ collapsible }) =>
    collapsible === 'never'
      ? 'display: none;'
      : collapsible === 'always'
      ? 'display: block;'
      : `@media only screen and (max-width: ${collapsible}px) {
    display: block;
  }`};
`;

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addons?: any; // FIXME
  bodyClassName?: string;
  children?: React.ReactNode;
  className?: string;
  collapsible?: Collapsible;
  description?: string;
  noPadding?: boolean;
  title?: string;
}

const FormSection = (props: Props) => {
  const { bodyClassName, collapsible, description, noPadding, title } = props;
  const AddonComponents = props.addons;
  const [isCollapsed, setIsCollapsed] = React.useState(true);
  const onCollapseChange = React.useCallback(() => setIsCollapsed(!isCollapsed), [isCollapsed]);
  const isActuallyCollapsed = !!collapsible && collapsible !== 'never' && isCollapsed;

  return (
    <Section className={props.className}>
      {title && (
        <Header onClick={onCollapseChange}>
          <Title>{title}</Title>
          <Addons>{AddonComponents && <AddonComponents />}</Addons>
          <CollapseIcon
            className={`fa fa-caret-${isCollapsed ? 'down' : 'up'}`}
            collapsible={collapsible || 'never'}
          />
        </Header>
      )}
      <Body
        className={bodyClassName}
        collapsible={collapsible || 'never'}
        isCollapsed={isActuallyCollapsed}
        noPadding={!!noPadding}
      >
        {description && <Description>{description}</Description>}
        {props.children}
      </Body>
    </Section>
  );
};

export default FormSection;
